import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useEffect, useRef } from 'react';
import { PushNotifications, ActionPerformed, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { useDeviceTokens } from './useDeviceTokens';
import { useAppName } from './useAppName';

/**
 * Force a fresh FCM registration. Safe to call multiple times.
 * The plugin will emit `registration` again with the current token (may be same value),
 * which lets the backend bump `inserted_at` and reset `consecutive_failures`.
 */
async function triggerRegister() {
  try {
    await PushNotifications.register();
  } catch (err) {
    console.warn('⚠️ PushNotifications.register() failed:', err);
  }
}

export function usePushNotifications(userId: string | null) {
  const { registerToken } = useDeviceTokens();
  const appName = useAppName();
  const listenersAttached = useRef(false);
  const lastRegisterAt = useRef<number>(0);

  useEffect(() => {
    if (!userId) {
      console.log('✅ No user logged in, skipping push setup');
      return;
    }

    if (Capacitor.getPlatform() === 'web') {
      console.log('🌐 PushNotifications not available on web');
      return;
    }

    let cancelled = false;
    let appStateHandle: { remove: () => Promise<void> } | null = null;

    const attachListenersOnce = async () => {
      if (listenersAttached.current) return;

      PushNotifications.addListener('registration', async (token: Token) => {
        console.log(`✅ [Capacitor Plugin] registration event: ${token.value.substring(0, 20)}..., app: ${appName}`);
        lastRegisterAt.current = Date.now();
        // Always upsert — bumps inserted_at (freshness heartbeat) and resets failure counter server-side.
        await registerToken(token.value, userId, appName);
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error('❌ Registration error:', error);
      });

      PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
        console.log('📥 Notification received:', notification);
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
        console.log('🔗 Notification action performed:', action);
      });

      listenersAttached.current = true;
    };

    const setup = async () => {
      try {
        console.log('⚡️ Requesting push permissions...');
        const result = await PushNotifications.requestPermissions();
        console.log('📜 Push permission result:', result);

        if (result.receive !== 'granted') {
          console.warn('❌ Permission not granted for push notifications');
          return;
        }

        await attachListenersOnce();
        if (cancelled) return;

        await triggerRegister();
        console.log('⚡️ Initial PushNotifications.register() called');
      } catch (error) {
        console.error('⚠️ Error setting up push notifications:', error);
      }
    };

    setup();

    // Refresh token on foreground if last register > 24h ago.
    // Silent FCM token rotation while the app is closed is the #1 cause of
    // "coach stops receiving notifications" — this catches it on next open.
    const STALE_MS = 24 * 60 * 60 * 1000;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      const age = Date.now() - lastRegisterAt.current;
      if (age > STALE_MS) {
        console.log(`🔄 Token age ${Math.round(age / 3600000)}h > 24h, refreshing FCM registration`);
        triggerRegister();
      }
    }).then((handle) => {
      appStateHandle = handle;
    });

    return () => {
      cancelled = true;
      appStateHandle?.remove().catch(() => {});
    };
  }, [registerToken, userId, appName]);
}
